import { Link, useLocation } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/auth'
import { useSidebarStore } from '@/stores/sidebar'
import { 
  BarChart3, 
  LayoutGrid, 
  FileBarChart, 
  History, 
  Store, 
  Users, 
  Settings, 
  LogOut,
  DollarSign,
  Zap,
  AlertTriangle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useThemeStore } from '@/stores/theme'
import { useState } from 'react'

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutGrid },
  { name: 'Weekly Reports', href: '/weekly', icon: FileBarChart },
  { name: 'Monthly Reports', href: '/monthly', icon: FileBarChart },
  { name: 'Performance', href: '/performance', icon: History },
  { name: 'Stores', href: '/stores', icon: Store },
  { name: 'Employees', href: '/employees', icon: Users },
  { name: 'Payroll Report', href: '/payroll', icon: DollarSign },
  { name: 'Auto Reporting', href: '/auto-reporting', icon: Zap },
  { name: 'Exceptions', href: '/exceptions', icon: AlertTriangle },
  { name: 'Settings', href: '/settings', icon: Settings },
]

export function Sidebar() {
  const location = useLocation()
  const { logout } = useAuthStore()
  const { isCollapsed, toggleSidebar } = useSidebarStore()
  const { isDarkMode } = useThemeStore()
  const [isHovered, setIsHovered] = useState(false)

  const handleLogout = async () => {
    await logout()
    window.location.href = '/login'
  }

  // Keep blue in light mode, use appropriate dark color for dark mode
  const sidebarBackground = isDarkMode ? '#1E293B' : '#0A2472' // Dark mode: slate-800, Light mode: original blue

  return (
    <div 
      className={cn(
        "fixed left-0 top-0 bottom-0 z-50 flex flex-col text-white transition-all duration-300",
        isCollapsed ? "w-[72px]" : "w-[250px]"
      )}
      style={{ backgroundColor: sidebarBackground }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        {!isCollapsed && (
          <div className="flex items-center space-x-3">
            <BarChart3 size={28} color="#FFFFFF" strokeWidth={2.5} />
            <div>
              <h1 className="text-lg font-inter-bold text-white">Falcon Holdings</h1>
              <p className="text-xs font-inter-regular text-white/70">Business Intelligence</p>
            </div>
          </div>
        )}
        {isCollapsed && (
          <div className="flex justify-center w-full">
            <BarChart3 size={24} color="#FFFFFF" strokeWidth={2.5} />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href
          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                "flex items-center px-3 py-3 rounded-lg text-sm font-inter-medium transition-colors cursor-pointer",
                isActive
                  ? "bg-white/15 text-white shadow-sm"
                  : "text-white/80 hover:bg-white/10 hover:text-white",
                isCollapsed ? "justify-center" : "space-x-3"
              )}
            >
              <item.icon size={20} className="flex-shrink-0" />
              {!isCollapsed && <span>{item.name}</span>}
            </Link>
          )
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/10">
        <button
          onClick={handleLogout}
          className={cn(
            "flex items-center w-full px-3 py-2 text-sm font-inter-medium text-white/80 hover:bg-white/10 hover:text-white rounded-lg transition-colors",
            isCollapsed ? "justify-center" : "space-x-3"
          )}
        >
          <LogOut size={20} />
          {!isCollapsed && <span>Sign Out</span>}
        </button>
      </div>

      {/* Collapse Button */}
      {(isHovered || !isCollapsed) && (
        <button
          onClick={toggleSidebar}
          className="absolute -right-6 top-1/2 transform -translate-y-1/2 w-6 h-10 flex items-center justify-center rounded-r-md transition-colors z-50"
          style={{ backgroundColor: sidebarBackground }}
        >
          {isCollapsed ? (
            <ChevronRight size={16} color="#FFFFFF" />
          ) : (
            <ChevronLeft size={16} color="#FFFFFF" />
          )}
        </button>
      )}
    </div>
  )
}
